'use client'

import { useState } from 'react'
import LoginForm from 'components/AuthComponents/LoginForm'
import OtpLoginForm from 'components/AuthComponents/OtpLoginForm'
import Link from 'components/shared/link'

interface LoginPageClientProps {
    lang: string
    passwordLoginTexts: {
        email: string
        password: string
        login: string
        loggingIn: string
        pleaseEnterYourPassword: string
        forgotPassword: string
        loggedInSuccessfullyRedirecting: string
        invalid_grant: string
        email_not_verified: string
    }
    otpLoginTexts: {
        email: string
        otpCode: string
        login: string
        loggingIn: string
        requestOtp: string
        requestingOtp: string
        verifyOtp: string
        verifyingOtp: string
        enterOtpCode: string
        otpSent: string
        loggedInSuccessfullyRedirecting: string
        invalidOtp: string
    }
    translations: {
        loginWithPassword: string
        loginWithOtp: string
        dontHaveAnAccountSignup: string
        signup: string
        IDontKnowMyPassword: string
        login: string
        activationSuccess: string
        accountAlreadyActive: string
    }
    activated?: string | string[] | null
}

export default function LoginPageClient({
    lang,
    passwordLoginTexts,
    otpLoginTexts,
    translations,
    activated
}: LoginPageClientProps) {
    const [loginMode, setLoginMode] = useState<'password' | 'otp'>('password')

    const showActivationMessage = activated === 'success' || activated === 'already_active'
    const activationMessage = activated === 'success' 
        ? translations.activationSuccess 
        : activated === 'already_active'
        ? translations.accountAlreadyActive
        : null

    return (
        <div className='flex flex-col gap-4 pb-10 w-full max-w-[500px] mx-auto'>
            <p className='font-extrabold text-xl md:text-2xl mt-0 md:mt-0'>{translations.login}</p>
            {showActivationMessage && activationMessage && (
                <div className='p-4 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm'>
                    {activationMessage}
                </div>
            )}
            <div className='mt-4'>
                {loginMode === 'password' ? (
                    <LoginForm lang={lang} texts={passwordLoginTexts} />
                ) : (
                    <OtpLoginForm lang={lang} texts={otpLoginTexts} />
                )}
            </div>
            <div className='flex flex-col gap-3'>
                <div className='flex items-center justify-center'>
                    <button
                        type='button'
                        onClick={() => setLoginMode(loginMode === 'password' ? 'otp' : 'password')}
                        className='text-sm text-muted-foreground hover:text-foreground underline'
                    >
                        {loginMode === 'password'
                            ? translations.loginWithOtp
                            : translations.loginWithPassword}
                    </button>
                </div>
                <div className='flex items-center gap-2 text-sm justify-between'>
                    <div className='flex items-center gap-2 text-sm justify-between'>
                        <Link href={`/signup`}>
                            <div className='flex items-center gap-1'>
                                <div>{translations.dontHaveAnAccountSignup}</div>
                                <span className='font-bold underline'>{translations.signup}</span>
                            </div>
                        </Link>
                    </div>
                    {loginMode === 'password' && (
                        <Link href={`/reset-password`}>
                            <div className='font-medium flex items-center gap-1'>
                                <div className='flex items-center'>{translations.IDontKnowMyPassword}</div>
                            </div>
                        </Link>
                    )}
                </div>
            </div>
        </div>
    )
}
